import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SectionHeading } from "@/components/Primitives";
import api from "@/lib/api";
import { ArrowRight, Monitor, Smartphone, Check } from "lucide-react";

const CATS = ["All", "Jewellery", "Healthcare", "Restaurants", "Retail", "Services"];

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);
  useEffect(() => { api.get("/public/portfolio").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const filtered = cat === "All" ? items : items.filter((i) => i.category === cat);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <SectionHeading eyebrow="Portfolio" title="Concept demos that convert"
        subtitle="Filter by industry. Every sample is clearly labelled as a concept demo — real client work is shown only when provided and approved." />

      <div className="flex flex-wrap justify-center gap-2 mb-10">
        {CATS.map((c) => (
          <button key={c} data-testid={`portfolio-filter-${c.toLowerCase()}`} onClick={() => setCat(c)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${cat === c ? "bg-violet-600 text-white" : "glass text-slate-300 hover:text-white"}`}>{c}</button>
        ))}
      </div>

      <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {filtered.map((p) => (
            <motion.div key={p.id} layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="glass rounded-2xl overflow-hidden card-hover" data-testid={`portfolio-card-${p.id}`}>
              <div className="relative">
                <img src={p.image} alt={p.title} className="w-full h-52 object-cover" loading="lazy" />
                {p.is_concept_demo && <span className="absolute top-3 left-3 text-[10px] font-semibold bg-amber-500/90 text-[#3a2800] px-2 py-1 rounded-full">Concept Demo</span>}
              </div>
              <div className="p-5">
                <div className="text-xs font-mono text-cyan-300 mb-1">{p.category}</div>
                <h3 className="font-semibold text-white text-lg mb-3">{p.title}</h3>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/10" onClick={() => setActive(p)} data-testid={`portfolio-view-${p.id}`}>View Details</Button>
                  <Link to={`/contact?ref=${encodeURIComponent(p.title)}`}><Button size="sm" className="bg-violet-600 hover:bg-violet-500 text-white">Request Similar</Button></Link>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="glass-strong border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">{active.title}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-3 gap-3">
                <img src={active.image} alt="desktop" className="col-span-2 rounded-xl h-40 object-cover w-full" />
                <img src={active.mobile_image || active.image} alt="mobile" className="rounded-xl h-40 object-cover w-full" />
              </div>
              <div className="flex gap-4 text-xs text-slate-400 mt-1">
                <span className="flex items-center gap-1"><Monitor className="w-3.5 h-3.5" /> Desktop</span>
                <span className="flex items-center gap-1"><Smartphone className="w-3.5 h-3.5" /> Mobile</span>
              </div>
              <div>
                <div className="text-xs font-mono uppercase text-violet-300 mb-2 mt-2">Features delivered</div>
                <div className="flex flex-wrap gap-2">{(active.features || []).map((f) => (<span key={f} className="text-xs bg-white/5 text-slate-200 px-2.5 py-1 rounded-md flex items-center gap-1"><Check className="w-3 h-3 text-emerald-400" />{f}</span>))}</div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-2">
                <div><div className="text-xs font-mono uppercase text-cyan-300 mb-1">Business problem</div><p className="text-sm text-slate-300">{active.problem}</p></div>
                <div><div className="text-xs font-mono uppercase text-emerald-300 mb-1">Proposed solution</div><p className="text-sm text-slate-300">{active.solution}</p></div>
              </div>
              <div className="flex gap-3 mt-3">
                <Link to={`/contact?ref=${encodeURIComponent(active.title)}`} className="flex-1"><Button className="w-full bg-violet-600 hover:bg-violet-500 text-white">Request Similar Website <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
